import * as React from "react";

import DocumentSummary from "../Document/Summary";

import classNames from "classnames";

interface CategorySummaryProps extends CategorySnakeModel {
  currentUser: UserSnakeModel,
  currentRoleDefinition: RoleDefinitionSnakeModel,
  reloadCategories: () => void,
}

type CategorySummaryDirection = "higher" | "lower"

export default function CategorySummary(props: CategorySummaryProps) {
  const [isExpanded, setIsExpanded] = React.useState(false);

  const handleSeeAllClicked = () => {
    setIsExpanded(!isExpanded);

    // Follow-through update event to Amity
    const data = {
      userId: props.currentUser.id,
      categoryId: props.id,
      event: "category_uncollapsed",
      value: !isExpanded,
    };

    fetch(`/categories/${props.id}/record_events_amity`, {
      method: "POST",
      body: JSON.stringify(data),
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then((response) => {
        if (!response.ok) { throw new Error(`${response.status} response`); }

        return response.json();
      })
      .then(() => {})
      .catch(() => {});
  };

  const handleCategoryClicked = () => {
    const data = {
      userId: props.currentUser.id,
      categoryId: props.id,
      event: "category_entered",
    };

    fetch(`/categories/${props.id}/record_events_amity`, {
      method: "POST",
      body: JSON.stringify(data),
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then((response) => {
        if (!response.ok) { throw new Error(`${response.status} response`); }

        return response.json();
      })
      .then(() => {})
      .catch(() => {});
  };

  const handleMoveCategory = (direction: CategorySummaryDirection) => {
    const data = {
      direction: direction,
      userId: props.currentUser.id,
    };

    let url;
    if (!props.favorited_by_user) {
      url = `/categories/${props.id}/move_category`;
    } else {
      url = `/category_users/${props.category_user_id}/move_category`;
    }

    fetch(url, {
      method: "POST",
      body: JSON.stringify(data),
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then((response) => {
        if (!response.ok) { throw new Error(`${response.status} response`); }

        return response.json();
      })
      .then(props.reloadCategories)
      .catch(() => {});
  };

  const handleFavoriteCategory = () => {
    const data = {
      userId: props.currentUser.id,
    };

    fetch(`/categories/${props.id}/favorite_category`, {
      method: "POST",
      body: JSON.stringify(data),
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then((response) => {
        if (!response.ok) { throw new Error(`${response.status} response`); }

        return response.json();
      })
      .then(props.reloadCategories)
      .catch(() => {});
  };

  if (props.hidden && !props.currentUser.is_admin) {
    return null;
  }

  const title = props.name == "Randomizer" ? <h3> {props.name} </h3> : <a href={`/categories/${props.id}`} onClick={handleCategoryClicked}> <h3> {props.name} </h3> </a>;

  const randomizerButton = props.name == "Randomizer" && (
    <button title="Shuffle" className="button bg-sky-600 hover:bg-sky-500 category-control" onClick={() => props.reloadCategories()}>
      <i className="fa fa-random" />
    </button>
  );

  let favoriteButtonClass = "button category-control";
  if (props.currentUser.is_admin) {
    favoriteButtonClass += " disabled";
  } else {
    favoriteButtonClass += ` ${props.favorited_by_user ? "favorited" : ""}`;
  }

  const favoriteButton = (
    <button title="Favorite" className={favoriteButtonClass} onClick={() => handleFavoriteCategory()} disabled={props.currentUser.is_admin}>
      <i className="fa fa-star" />
    </button>
  );

  const expandButton = (
    <button title={isExpanded ? "Hide" : `See All ${props.documents.length}`} className="button category-control bg-sky-600 hover:bg-sky-500" onClick={() => handleSeeAllClicked()}>
      <i className={`fa fa-${isExpanded ? "down-left-and-up-right-to-center" : "up-right-and-down-left-from-center"}`} />
    </button>
  );

  let moveUpButtonClass = "button category-control";
  if (props.top_of_list) {
    moveUpButtonClass += " disabled";
  } else {
    moveUpButtonClass += " bg-sky-600 hover:bg-sky-500";
  }
  const moveUpButton = (
    <button title="Move up" className={moveUpButtonClass} onClick={() => handleMoveCategory("higher")} disabled={props.top_of_list}>
      <i className="fa fa-arrow-up" />
    </button>
  );

  let moveDownButtonClass = "button category-control";
  if (props.bottom_of_list) {
    moveDownButtonClass += " disabled";
  } else {
    moveDownButtonClass += " bg-sky-600 hover:bg-sky-500";
  }
  const moveDownButton = (
    <button title="Move down" className={moveDownButtonClass} onClick={() => handleMoveCategory("lower")} disabled={props.bottom_of_list}>
      <i className="fa fa-arrow-down" />
    </button>
  );

  const canAddPositionControls = (props.currentUser.is_admin || (!props.currentUser.is_admin && props.favorited_by_user));
  const categoryControlsContainer = (
    <div className="category-control-container">
      {expandButton}
      {randomizerButton}
      {favoriteButton}
      {canAddPositionControls && moveUpButton}
      {canAddPositionControls && moveDownButton}
    </div>
  );

  let documents;
  if (props.documents && props.documents.length > 0) {
    let documentsToRender = props.documents;
    if (!isExpanded) {
      documentsToRender = documentsToRender.slice(0, 3);
    }

    documents = (
      <div
        id={`category-list-${props.id}`}
        className={classNames("category-container-covers", {"expanded": isExpanded})}
      >
        {documentsToRender.map((doc) => {
          return (
            <DocumentSummary
              document={doc}
              key={doc.id}
              canAddFromSummary
              currentUser={props.currentUser}
              currentRoleDefinition={props.currentRoleDefinition}
              categoryId={props.id}
            />
          );
        })}
      </div>
    );
  } else {
    documents = <p> This category has no cases to display yet. </p>;
  }

  return (
    <div className={`category-container ${props.hidden && "hidden-category-panel"}`}>
      <div className="category-container-covers-container">
        <div className="category-container-header" style={{"marginBottom": 15}}>
          {title}
        </div>

        {documents}
      </div>

      {categoryControlsContainer}
    </div>
  );
}
